/**
 * D8 tile-menu entries exporting a chart tile's rendered chart as an image
 * (PNG/SVG), reusing the charts feature's `exportChart` pipeline end-to-end.
 * The live SVG is pulled from the mounted `ChartTileView` via the chart-SVG
 * registry (the renderer's ref lives in a different subtree). Items disable
 * with a WHY tooltip until the chart has data, matching TileDataExportMenuItems.
 */
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { exportChart } from '../../../charts/services/chartExportApi';
import type { ChartExportFormat } from '../../../charts/types/chartTypes';
import { getChartSvgContent } from '../../services/chartSvgRegistry';
import { tileRuntimeKey, useDashboardRuntimeStore } from '../../store/dashboardRuntimeStore';
import type { ChartTile } from '../../types/dashboardTypes';

const NO_DATA_WHY = 'Run the tile first — there is no chart to export';

export function ChartImageExportMenuItems({
  dashboardId,
  tile,
}: {
  dashboardId: string;
  tile: ChartTile;
}) {
  const { track } = useAnalytics();
  const key = tileRuntimeKey(dashboardId, tile.id);
  const lastSuccess = useDashboardRuntimeStore((s) => s.byTile[key]?.lastSuccess);
  const why = lastSuccess ? undefined : NO_DATA_WHY;

  const exportImage = (format: ChartExportFormat) => async () => {
    const svgContent = getChartSvgContent(key);
    if (!svgContent) {
      toast.error({ title: 'Export failed', description: 'Could not read the chart content' });
      return;
    }
    const suggestedFileName = tile.title.replace(/[^a-zA-Z0-9]+/g, '-') || 'chart';
    const result = await exportChart(svgContent, format, suggestedFileName);
    if (result.success) {
      track('feature:dashboard_chart_export', { format });
    } else if (!result.cancelled) {
      toast.error({ title: 'Export failed', description: result.error ?? 'Unknown error' });
    }
  };

  return (
    <>
      <DropdownMenuSeparator />
      <span title={why}>
        <DropdownMenuItem
          disabled={!!why}
          onClick={() => void exportImage('png')()}
          data-testid="tile-export-chart-png-item"
        >
          Export chart as PNG…
        </DropdownMenuItem>
      </span>
      <span title={why}>
        <DropdownMenuItem
          disabled={!!why}
          onClick={() => void exportImage('svg')()}
          data-testid="tile-export-chart-svg-item"
        >
          Export chart as SVG…
        </DropdownMenuItem>
      </span>
    </>
  );
}
